import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, Trash2, Package, CreditCard, Megaphone, Bike } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Screen, TopBar } from './PhoneFrame';

const STYLES = {
  order: { icon: Package, bg: 'bg-[#FDECEC]', fg: 'text-[#EE3B36]' },
  payment: { icon: CreditCard, bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  driver: { icon: Bike, bg: 'bg-blue-50', fg: 'text-blue-600' },
  promo: { icon: Megaphone, bg: 'bg-amber-50', fg: 'text-amber-600' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'Ahora';
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'Ayer' : `Hace ${d} días`;
};

const NotificationRow = ({ n, onOpen, onDelete }) => {
  const s = STYLES[n.type] || STYLES.order;
  const Icon = s.icon;
  return (
    <li data-testid={`cnotif-${n.id}`} className={`flex items-start gap-3 px-4 py-3.5 ${n.read ? '' : 'bg-[#FFF8F8]'}`}>
      <span className={`w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center ${s.bg} ${s.fg}`}><Icon size={19} /></span>
      <button onClick={() => onOpen(n)} className="flex-1 min-w-0 text-left press">
        <p className="font-bold text-[14px] text-[#1E2130] leading-snug flex items-center gap-1.5">
          {n.title}
          {!n.read && <span className="w-2 h-2 rounded-full bg-[#EE3B36] shrink-0" />}
        </p>
        <p className="text-[13px] text-gray-600 leading-snug mt-0.5">{n.desc}</p>
        <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt)}{n.orderId ? ` · #${n.orderId}` : ''}</p>
      </button>
      <button data-testid={`cnotif-delete-${n.id}`} onClick={() => onDelete(n.id)} className="w-8 h-8 rounded-lg text-gray-300 hover:text-red-500 flex items-center justify-center shrink-0 press"><Trash2 size={15} /></button>
    </li>
  );
};

const CustomerNotifications = () => {
  const navigate = useNavigate();
  const { customer, customerNotifications, customerUnread, markCustomerNotificationsRead, markCustomerNotificationRead, deleteCustomerNotification, clearCustomerNotifications, refreshCustomerNotifications } = useApp();

  useEffect(() => { refreshCustomerNotifications(customer?.id); }, [customer?.id, refreshCustomerNotifications]);

  const open = (n) => {
    if (!n.read) markCustomerNotificationRead(n.id);
    if (n.orderId) navigate(`/app/orders/${n.orderId}`);
  };

  return (
    <>
      <TopBar title="Notificaciones" right={customerUnread > 0 ? (
        <button data-testid="cnotif-read-all" onClick={() => markCustomerNotificationsRead(customer.id)} className="text-[#EE3B36] press"><CheckCheck size={20} /></button>
      ) : null} />
      <Screen className="pt-0" data-testid="customer-notifications">
        {customerNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-10 pt-24" data-testid="cnotif-empty">
            <span className="w-20 h-20 rounded-full bg-[#F7F8FA] flex items-center justify-center text-gray-300"><BellOff size={34} /></span>
            <p className="font-extrabold text-[17px] mt-4 text-[#1E2130]">Todo tranquilo por aquí</p>
            <p className="text-[13px] text-gray-500 mt-1">Te avisaremos cuando tu pago sea verificado, cuando el conductor salga y cuando tu pedido llegue.</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
              <Bell size={13} /> {customerUnread > 0 ? `${customerUnread} sin leer` : 'Al día'}
            </div>
            <ul className="divide-y divide-gray-100 border-t border-gray-100">
              {customerNotifications.map((n) => <NotificationRow key={n.id} n={n} onOpen={open} onDelete={deleteCustomerNotification} />)}
            </ul>
            <div className="px-4 py-5">
              <button data-testid="cnotif-clear" onClick={() => clearCustomerNotifications(customer.id)} className="w-full h-11 rounded-xl border border-gray-200 text-[13px] font-bold text-gray-500 press">Borrar todas</button>
            </div>
          </>
        )}
      </Screen>
    </>
  );
};

export default CustomerNotifications;
